import { useState } from "react";
import { unreadMessagesCountKey, useIsOrganizationChat } from "@api/chat.api";
import { tasksQueryKeys } from "@api/task.api";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useAgentRunLiveUpdates } from "@hooks/useAgentRunLiveUpdates";
import { useQueryClient } from "@tanstack/react-query";
import { useNatsSubscription } from "@hooks/useNatsSubscription";
import { useScope } from "@state/scopeContext.lib";
import { SubScopeProvider } from "@state/subScopeContext";

import classNames from "classnames";
import { ThreadWithWorkspaceName } from "@common/schemas/chat.schema";
import { getOrganizationTaskLink } from "@components/organizationView/clients-tasks/organizationTaskLink";
import { AgentRunsPanel } from "./agent/AgentRunsPanel";
import { Threads } from "./Threads";
import { useChat } from "./Chat.context";
import { Chat } from "./Chat";
import { useIsAgentTabShown } from "./useIsAgentTabShown";
import styles from "./Chat.module.scss";

export const ChatContainer: React.FC = () => {
    const { isOpen, panel } = useChat();
    const [selectedThread, setSelectedThread] = useState<
        ThreadWithWorkspaceName | undefined
    >(undefined);

    const activeWorkspace = useActiveWorkspace();
    const { activeWorkspaceKey, organizationId } = useScope();
    const isOrganizationChat = useIsOrganizationChat();
    // The panel choice outlives a move between pages; without the tab the
    // drawer falls back to the thread list.
    const isAgentTabShown = useIsAgentTabShown();
    const queryClient = useQueryClient();

    useAgentRunLiveUpdates();

    useNatsSubscription(
        activeWorkspace?.id
            ? `workspaces.${activeWorkspace.id}.notifications.threads.>`
            : null,
        {
            onMessage: () => {
                if (!activeWorkspace?.id) {
                    return;
                }
                queryClient.invalidateQueries({
                    queryKey: ["chat", "threads", activeWorkspace.id],
                });
                queryClient.invalidateQueries({
                    queryKey: unreadMessagesCountKey(activeWorkspaceKey),
                });
                queryClient.invalidateQueries({
                    queryKey:
                        tasksQueryKeys.threadUnreadCountsByWorkspaceKey(
                            activeWorkspaceKey,
                        ),
                    exact: false,
                });
            },
        },
    );

    useNatsSubscription(
        isOrganizationChat
            ? `organizations.${organizationId}.notifications.threads.updated`
            : null,
        {
            onMessage: () => {
                queryClient.invalidateQueries({ queryKey: ["chat"] });
            },
        },
    );

    const handleSelectThread = (thread?: ThreadWithWorkspaceName) => {
        setSelectedThread(thread);
        if (!thread && isOrganizationChat) {
            queryClient.invalidateQueries({ queryKey: ["chat"] });
        }
    };

    const renderPanel = () => {
        if (selectedThread) {
            return (
                <SubScopeProvider
                    value={{ workspaceId: selectedThread.workspaceId }}
                >
                    <Chat
                        selectedThread={selectedThread}
                        titleLink={
                            isOrganizationChat && selectedThread.taskId
                                ? getOrganizationTaskLink({
                                      taskId: selectedThread.taskId,
                                      workspaceId: selectedThread.workspaceId,
                                  })
                                : undefined
                        }
                        isOrganizationChat={isOrganizationChat}
                        setSelectedThread={handleSelectThread}
                    />
                </SubScopeProvider>
            );
        }
        if (panel === "agent" && isAgentTabShown) {
            return <AgentRunsPanel />;
        }
        return <Threads setSelectedThread={setSelectedThread} />;
    };

    return (
        <div
            className={classNames(styles.chatContainer, {
                [styles.open]: isOpen,
                [styles.organization]: isOrganizationChat,
            })}
        >
            {isOpen ? renderPanel() : null}
        </div>
    );
};
